import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActivity } from '../App';
import { useTheme } from '../context/ThemeContext';

const fmtTime = (ts) => {
  const diff = Date.now() - new Date(ts);
  if (diff < 60000)    return 'Just now';
  if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const Header = ({ onToggleSidebar, onMobileMenu }) => {
  const { user, logout } = useAuth();
  const { logs } = useActivity();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [readCount, setReadCount] = useState(0);
  const dropRef = useRef(null);
  const notifRef = useRef(null);

  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  // Show the 5 most recent activity logs as notifications
  const recentLogs = logs.slice(0, 5);
  const unreadCount = Math.max(0, recentLogs.length - readCount);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifPanel(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header style={styles.header}>
      <div style={styles.left}>
        {/* Desktop sidebar toggle */}
        <button style={styles.iconBtn} onClick={onToggleSidebar} className="desktop-only" title="Toggle Sidebar">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        {/* Mobile menu */}
        <button style={styles.iconBtn} onClick={onMobileMenu} className="mobile-only" title="Menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>

        {/* Search */}
        <div style={styles.searchWrap}>
          <svg style={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input style={styles.searchInput} type="text" placeholder="Search anything..." />
        </div>
      </div>

      <div style={styles.right}>
        {/* Theme Toggle */}
        <button
          style={{ ...styles.iconBtn, fontSize: '1.1rem' }}
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>

        {/* Notifications */}
        <div style={{ position: 'relative' }} ref={notifRef}>
          <button style={styles.iconBtn} onClick={() => { setShowNotifPanel(p => !p); setReadCount(recentLogs.length); }} title="Notifications">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {unreadCount > 0 && <span style={styles.notifBadge}>{unreadCount}</span>}
          </button>

          {showNotifPanel && (
            <div style={styles.notifPanel}>
              <div style={styles.notifHeader}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Recent Activity</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', cursor: 'pointer' }}
                  onClick={() => setReadCount(logs.length)}>Mark all read</span>
              </div>
              {recentLogs.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  No activity yet this session
                </div>
              ) : recentLogs.map((log, i) => (
                <div key={log.id || i} style={{ ...styles.notifItem, ...(i < unreadCount ? styles.notifUnread : {}) }}>
                  {i < unreadCount && <span style={styles.unreadDot} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                      {log.action}{log.target ? `: ${log.target}` : ''}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>{fmtTime(log.timestamp)}</div>
                  </div>
                </div>
              ))}
              <button style={styles.viewAllBtn} onClick={() => { navigate('/activity-logs'); setShowNotifPanel(false); }}>
                View All Activity Logs
              </button>
            </div>
          )}
        </div>

        {/* User dropdown */}
        <div style={{ position: 'relative' }} ref={dropRef}>
          <button style={styles.userBtn} onClick={() => setShowDropdown(p => !p)}>
            <div style={styles.avatar}>{initials}</div>
            <div style={styles.userMeta}>
              <span style={styles.userName}>{user?.name}</span>
              <span style={styles.userRole}>{user?.adminRole?.replace('_', ' ')}</span>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {showDropdown && (
            <div style={styles.dropdown}>
              <div style={styles.dropHeader}>
                <div style={styles.avatar}>{initials}</div>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{user?.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{user?.email}</div>
                </div>
              </div>
              <div style={styles.dropDivider} />
              {[
                { icon: '👤', label: 'My Profile',  path: '/profile' },
                { icon: '⚙️', label: 'Settings',    path: '/settings' },
                { icon: '📊', label: 'Reports',     path: '/reports' },
              ].map(item => (
                <button key={item.path} style={styles.dropItem}
                  onClick={() => { navigate(item.path); setShowDropdown(false); }}>
                  <span>{item.icon}</span> {item.label}
                </button>
              ))}
              <div style={styles.dropDivider} />
              <button style={{ ...styles.dropItem, color: 'var(--accent-danger)' }}
                onClick={() => { logout(); setShowDropdown(false); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const styles = {
  header: {
    position: 'fixed', top: 0, right: 0, left: 0,
    height: 'var(--header-height)',
    background: 'var(--bg-secondary)',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 20px',
    zIndex: 90,
    backdropFilter: 'blur(10px)',
    marginLeft: 'var(--sidebar-width)',
    transition: 'margin-left 0.3s ease',
  },
  left: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1 },
  right: { display: 'flex', alignItems: 'center', gap: '8px' },
  iconBtn: {
    width: '36px', height: '36px', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: 'var(--text-secondary)', position: 'relative',
    transition: 'background 0.2s',
  },
  notifBadge: {
    position: 'absolute', top: '4px', right: '4px',
    width: '16px', height: '16px', borderRadius: '50%',
    background: 'var(--accent-danger)', color: 'white',
    fontSize: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700,
  },
  searchWrap: { position: 'relative', flex: 1, maxWidth: '320px' },
  searchIcon: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' },
  searchInput: {
    width: '100%', padding: '8px 12px 8px 34px',
    background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
    borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.85rem',
    outline: 'none',
  },
  userBtn: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '4px 10px 4px 4px',
    background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
    borderRadius: '10px', cursor: 'pointer',
    transition: 'background 0.2s',
  },
  avatar: {
    width: '30px', height: '30px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 600, fontSize: '0.75rem', flexShrink: 0,
  },
  userMeta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  userName: { fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2 },
  userRole: { fontSize: '0.7rem', color: 'var(--accent-secondary)', lineHeight: 1.2 },
  dropdown: {
    position: 'absolute', top: '44px', right: 0,
    width: '220px', background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)', borderRadius: '12px',
    boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
    animation: 'slideUp 0.2s ease',
    zIndex: 200,
  },
  dropHeader: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '14px 16px',
  },
  dropDivider: { height: '1px', background: 'var(--border-color)', margin: '4px 0' },
  dropItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    width: '100%', padding: '10px 16px',
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-secondary)', fontSize: '0.875rem',
    transition: 'background 0.15s',
    textAlign: 'left',
  },
  notifPanel: {
    position: 'absolute', top: '44px', right: 0,
    width: '320px', background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)', borderRadius: '12px',
    boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
    animation: 'slideUp 0.2s ease',
    zIndex: 200,
  },
  notifHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
  },
  notifItem: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '12px 16px', borderBottom: '1px solid var(--border-color)',
    transition: 'background 0.15s', cursor: 'pointer',
  },
  notifUnread: { background: 'rgba(99,102,241,0.06)' },
  unreadDot: {
    width: '7px', height: '7px', borderRadius: '50%',
    background: 'var(--accent-primary)', flexShrink: 0, marginTop: '5px',
  },
  viewAllBtn: {
    width: '100%', padding: '12px',
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--accent-primary)', fontSize: '0.82rem', fontWeight: 500,
    textAlign: 'center',
  },
};

export default Header;
